import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PublicContact.css';

const PublicContact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'registration_issue',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const issueTypes = [
    { value: 'registration_issue', label: 'Registration Problem', icon: '🚫' },
    { value: 'login_issue', label: 'Login Problem', icon: '🔐' },
    { value: 'verification_issue', label: 'Verification Issue', icon: '✉️' },
    { value: 'technical_issue', label: 'Technical Problem', icon: '🐛' },
    { value: 'question', label: 'General Question', icon: '❓' },
    { value: 'other', label: 'Other', icon: '💬' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/feedback/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.message || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (success) {
    return (
      <div className="public-contact-container">
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2>Thank You!</h2>
          <p>We've received your message and will get back to you soon.</p>
          <p>Please check your email for our response.</p>
          <div className="success-actions">
            <button onClick={() => navigate('/login')} className="btn-primary">
              Go to Login
            </button>
            <button onClick={() => navigate('/register')} className="btn-secondary">
              Try Registration Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-contact-container">
      <div className="public-contact-header">
        <h1>💜 Belong - Get Help</h1>
        <p>Having trouble registering or logging in? We're here to help!</p>
      </div>

      <form onSubmit={handleSubmit} className="public-contact-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">
              Your Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your.email@example.com"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phone">
            Phone Number (Optional)
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1234567890"
          />
        </div>

        <div className="form-group">
          <label htmlFor="type">What do you need help with?</label>
          <div className="type-selector">
            {issueTypes.map(type => (
              <label key={type.value} className={`type-option ${formData.type === type.value ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="type"
                  value={type.value}
                  checked={formData.type === type.value}
                  onChange={handleChange}
                />
                <span className="type-icon">{type.icon}</span>
                <span className="type-label">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="subject">
            Subject <span className="required">*</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            maxLength={100}
            required
            placeholder="Brief summary of your issue"
          />
          <div className="char-count">{formData.subject.length}/100</div>
        </div>

        <div className="form-group">
          <label htmlFor="message">
            Describe Your Issue <span className="required">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            maxLength={1000}
            required
            rows={8}
            placeholder="Please provide as much detail as possible about the problem you're experiencing..."
          />
          <div className="char-count">{formData.message.length}/1000</div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/login')} className="btn-secondary">
            Back to Login
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Sending...' : 'Send Message'}
          </button>
        </div>

        <div className="rate-limit-notice">
          <small>Limited to 3 submissions per hour to prevent spam.</small>
        </div>
      </form>

      <div className="help-links">
        <p>Already have an account? <a href="/login">Login here</a></p>
        <p>Want to create an account? <a href="/register">Register here</a></p>
      </div>
    </div>
  );
};

export default PublicContact;
